import { useTranslation } from "react-i18next";
import {
  TModalsState,
  setMapStateData,
  setModalsData,
} from "../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppAvatar from "../../components/avatar";
import { getUserIntials } from "../../utils/string";
import { Formik } from "formik";
import {
  layerFilterInitialValues,
  LayerFilterValidationSchema,
} from "./validation";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldSubmit,
} from "../../components/admin/formFields";
import { TMapLayerOptions, TMapState, TMapType } from "../../types/map";
import { FC } from "react";
import { routeUrls } from "../../navigation/routeUrls";
import { APP_CONFIG } from "../../constants/constants";

// Map type icon images
// source: https://learn.microsoft.com/en-us/bingmaps/v8-web-control/map-control-api/maptypeid-enumeration
import MapTypeRoad from "../../assets/svg/mapTypeRoad.svg";
// import MapTypeSatellite from "../../assets/svg/mapTypeSatellite.svg";
import MapTypeSatellite from "../../assets/images/mapTypeSatellite.png";
import MapTypeOrdnance from "../../assets/images/mapTypeOrdnance.png";
import MapTypeBirdseye from "../../assets/images/mapTypeBirdseye.jpeg";
import MapTypeStreetside from "../../assets/images/mapTypeStreetside.png";
import MapType3DB from "../../assets/images/mapType3DB.png";
import svgIcons from "../../assets/svg/svgIcons";
import { useLoggedInUserData } from "../../utils/user";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";

type TViewListItem = {
  id: number;
  name: string;
  src: string;
  mapType: TMapType;
  disabled?: boolean;
};

const ViewList: TViewListItem[] = [
  {
    id: 1,
    name: "Road",
    src: MapTypeRoad,
    mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
      .type as TMapType,
  },
  {
    id: 2,
    name: "Satellite",
    src: MapTypeSatellite,
    mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE]
      .type as TMapType,
  },
  // {
  //   id: 3,
  //   name: "Ordnance",
  //   src: MapTypeOrdnance,
  //   mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.HYBRID]
  //     .type as TMapType,
  // },
  {
    id: 4,
    name: "Bird's Eye",
    src: MapTypeBirdseye,
    mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.BIRDSEYE]
      .type as TMapType,
  },
  {
    id: 5,
    name: "Streetside",
    src: MapTypeStreetside,
    mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.STREETVIEW]
      .type as TMapType,
  },
  // {
  //   id: 6,
  //   name: "3D",
  //   src: MapType3DB,
  //   mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.THREED].type as TMapType,
  //   disabled: true
  // }
];

type ModalLayerFiltersProps = {
  onMapTypeChange: (mapType: TMapType) => void;
  onMapLayerChange: (mapLayerOptions: TMapLayerOptions) => void;
  onMapCloseClick: () => void;
};

const ModalLayerFilters: FC<ModalLayerFiltersProps> = ({
  onMapTypeChange,
  onMapLayerChange,
  onMapCloseClick,
}) => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "mapFleet.layerFilter",
  });
  const { getUserACLAccessLevel } = useACL();

  const clusteringEnabled = useLoggedInUserData("clusteringEnabled");

  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const hideModal = () => {
    dispatch(setModalsData({ ...modalsState, showLayerFilter: false }));
    onMapCloseClick();
  };

  const handleClickManageGeozones = () => {
    navigate(routeUrls.dashboardChildren.adminChildren.geozones);
    hideModal();
  };

  const handleLayerOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (!!name && typeof checked === "boolean") {
      onMapLayerChange({
        ...mapState.mapLayerOptions,
        [name]: checked,
      });
      setTimeout(() => hideModal(), 500); // just to give a feel of saving/processing and show toggle animation
    }
  };

  if (modalsState.showLayerFilter === false) return null;
  return (
    <>
      <div className="absolute p-6 rounded-lg gap-3 mx-auto max-w-[calc(100vw-4rem)] w-[588px] z-map-popup top-5 right-5 bg-white shadow-lg">
        <Formik
          initialValues={layerFilterInitialValues}
          validationSchema={LayerFilterValidationSchema}
          onSubmit={(values, { setSubmitting }) => {
            console.log(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            dirty,
            setFieldValue,
            setFieldTouched,
          }) => {
            return (
              <form className="gap-5">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold leading-6 text-heading-black">
                    {t("heading")}
                  </h2>
                  <button type="button" onClick={hideModal}>
                    <img
                      src={svgIcons.CloseIcon}
                      alt="close-icon"
                      className="cursor-pointer"
                    />
                  </button>
                </div>
                <div className="p-4 flex justify-between">
                  {ViewList?.map((item, index) => (
                    <button
                      type="button"
                      key={index}
                      className={`group gap-2 items-center justify-center${
                        item.disabled && " opacity-50 grayscale"
                      }`}
                      disabled={item.disabled}
                      onClick={
                        item.disabled
                          ? () => {}
                          : () => {
                              setFieldValue("view", item.name);
                              onMapTypeChange(item.mapType as TMapType);
                              hideModal();
                            }
                      }
                    >
                      <img
                        src={item.src}
                        alt="road-map-view"
                        className={`size-14 rounded-full border-2 ${item.mapType === mapState?.mapType ? "border-accent-blue-dark" : item.disabled ? "" : "group-hover:border-accent-blue-bright group-focus:border-accent-blue-bright"} `}
                      />
                      <p
                        className={`font-semibold text-xs leading-10 text-center ${item.mapType === mapState?.mapType ? "text-accent-blue-dark" : item.disabled ? "" : "group-hover:text-accent-blue-bright group-focus:text-accent-blue-bright text-gray-400"}`}
                      >
                        {item.name}
                      </p>
                    </button>
                  ))}
                </div>
                <div className="space-y-4">
                  <p className="font-semibold text-lg leading-5">
                    {t("additional_filters")}
                  </p>
                  <div className="grid grid-col-8 gap-4">
                    <AdminFormFieldCheckbox
                      label={t("traffic")}
                      id="traffic"
                      type="checkbox"
                      name="traffic"
                      checked={mapState.mapLayerOptions?.traffic}
                      onChange={handleLayerOptionChange}
                      onBlur={handleBlur}
                      touched={touched.traffic}
                      error={errors.traffic}
                    />

                    <AdminFormFieldCheckbox
                      label={t("weather")}
                      id="weather"
                      type="checkbox"
                      name="weather"
                      checked={mapState.mapLayerOptions?.weather}
                      onChange={handleLayerOptionChange}
                      onBlur={handleBlur}
                      touched={touched.weather}
                      error={errors.weather}
                    />

                    {/* <AdminFormFieldCheckbox
                      label={t("three_d_building")}
                      id="three_d_building"
                      type="checkbox"
                      name="three_d_building"
                      checked={mapState.mapLayerOptions?.three_d_building}
                      onChange={handleLayerOptionChange}
                      onBlur={handleBlur}
                      touched={touched.three_d_building}
                      error={errors.three_d_building}
                      disabled={true}
                    /> */}

                    <AdminFormFieldCheckbox
                      label={t("clustering")}
                      id="clustering"
                      type="checkbox"
                      name="clustering"
                      checked={mapState.mapLayerOptions?.clustering}
                      onChange={handleLayerOptionChange}
                      onBlur={handleBlur}
                      touched={touched.clustering}
                      error={errors.clustering}
                      disabled={clusteringEnabled === "0"}
                    />

                    <AdminFormFieldCheckbox
                      label={t("hide_geozones")}
                      id="hide_geozones"
                      type="checkbox"
                      name="hide_geozones"
                      checked={mapState.mapLayerOptions?.hide_geozones}
                      onChange={handleLayerOptionChange}
                      onBlur={handleBlur}
                      touched={touched.hide_geozones}
                      error={errors.hide_geozones}
                      // customWrapperClass="col-span-8"
                    />
                    {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_GEOZONE) >=
                      ROLE_ACCESS_LEVELS.READ && (
                      <div className="col-span-8">
                        <AdminFormFieldSubmit
                          label={t("manage_geozones")}
                          type="submit"
                          variant="primary"
                          onClick={handleClickManageGeozones}
                          disabled={isSubmitting}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </form>
            );
          }}
        </Formik>
      </div>
    </>
  );
};

export default ModalLayerFilters;
